import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7a0fe360"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "pdf-viewer__toolbar" }
const _hoisted_2 = {
  id: "pdf-viewer-title",
  class: "pdf-viewer__title"
}
const _hoisted_3 = { class: "pdf-viewer__controls" }
const _hoisted_4 = ["disabled", "aria-label", "title"]
const _hoisted_5 = ["disabled", "aria-label", "title"]
const _hoisted_6 = ["max", "disabled", "aria-label", "onKeydown"]
const _hoisted_7 = { class: "pdf-viewer__page-total" }
const _hoisted_8 = ["disabled", "aria-label", "title"]
const _hoisted_9 = ["disabled", "aria-label", "title"]
const _hoisted_10 = { class: "pdf-viewer__close-wrap" }
const _hoisted_11 = ["aria-label", "title", "onClick"]

import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from "vue";
import { t } from "@/i18n";

type ViewerStatus = "loading" | "ready" | "error";

interface PdfPageState {
  pageNumber: number;
  node: HTMLDivElement;
  page: any;
  renderTask: any;
  queued: boolean;
  rendering: boolean;
  rendered: boolean;
}

interface PdfViewerSession {
  disposed: boolean;
  loadingTask: any;
  document: any;
  observer: IntersectionObserver | null;
  resizeObserver: ResizeObserver | null;
  resizeTimer: number | null;
  resizeHandler: (() => void) | null;
  pageStates: Map<number, PdfPageState>;
  nearbyPages: Set<number>;
  renderQueue: number[];
  activeRenders: number;
  currentPage: number;
  navigationTarget: number | null;
  totalPages: number;
  viewerWidth: number;
}


export default /*#__PURE__*/_defineComponent({
  props: {
    show: null,
    source: null,
    title: null
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ({
  (event: "close"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  show: boolean;
  source: Blob | null;
  title: string;
}





const PDF_JS_VERSION = "6.1.200";
const baseUrl = String(process.env.BASE_URL || "/").replace(/\/?$/, "/");
const pdfJsBase = `${baseUrl}pdfjs/${PDF_JS_VERSION}`;
let pdfJsPromise: Promise<any> | null = null;

const viewerRoot = ref<HTMLElement | null>(null);
const viewerBody = ref<HTMLElement | null>(null);
const pagesContainer = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
const locallyClosed = ref(false);
const status = ref<ViewerStatus>("loading");
const errorKey = ref("pdfViewer.loadError");
const currentPage = ref(1);
const totalPages = ref(0);
const pageInput = ref("1");

let activeSession: PdfViewerSession | null = null;
let previousFocus: HTMLElement | null = null;

const isDisplayed = computed(() => props.show && !locallyClosed.value);
const canGoBack = computed(
  () => totalPages.value > 0 && currentPage.value > 1
);
const canGoForward = computed(
  () => totalPages.value > 0 && currentPage.value < totalPages.value
);
const statusMessage = computed(() =>
  status.value === "loading" ? t("pdfViewer.loading") : t(errorKey.value)
);

function loadPdfJs(): Promise<any> {
  if (!pdfJsPromise) {
    const moduleUrl = `${pdfJsBase}/pdf.min.mjs`;
    pdfJsPromise = import(
      /* webpackIgnore: true */ moduleUrl
    )
      .then((pdfJs) => {
        pdfJs.GlobalWorkerOptions.workerSrc = `${pdfJsBase}/pdf.worker.min.mjs`;
        return pdfJs;
      })
      .catch((error) => {
        pdfJsPromise = null;
        throw error;
      });
  }
  return pdfJsPromise;
}

function isActive(session: PdfViewerSession): boolean {
  return activeSession === session && !session.disposed;
}

function createPagePlaceholder(pageNumber: number): HTMLDivElement {
  const pageNode = document.createElement("div");
  pageNode.className = "pdf-viewer__page";
  pageNode.dataset.page = String(pageNumber);

  const label = document.createElement("span");
  label.className = "pdf-viewer__page-number";
  label.textContent = String(pageNumber);
  pageNode.appendChild(label);
  return pageNode;
}

function syncPageControls(session: PdfViewerSession, pageNumber: number) {
  if (!isActive(session) || !session.totalPages) return;
  const nextPage = Math.min(Math.max(pageNumber, 1), session.totalPages);
  session.currentPage = nextPage;
  currentPage.value = nextPage;
  pageInput.value = String(nextPage);
}

function queuePage(
  session: PdfViewerSession,
  pageNumber: number,
  priority = false
) {
  if (
    !isActive(session) ||
    pageNumber < 1 ||
    pageNumber > session.totalPages
  ) {
    return;
  }
  const pageState = session.pageStates.get(pageNumber);
  if (
    !pageState ||
    pageState.queued ||
    pageState.rendering ||
    pageState.rendered
  ) {
    return;
  }

  pageState.queued = true;
  if (priority) session.renderQueue.unshift(pageNumber);
  else session.renderQueue.push(pageNumber);
  drainRenderQueue(session);
}

function drainRenderQueue(session: PdfViewerSession) {
  if (!isActive(session)) return;
  while (session.activeRenders < 2 && session.renderQueue.length > 0) {
    const pageNumber = session.renderQueue.shift();
    if (pageNumber == null) break;
    const pageState = session.pageStates.get(pageNumber);
    if (!pageState || !pageState.queued || pageState.rendered) continue;

    pageState.queued = false;
    session.activeRenders += 1;
    void renderPage(session, pageState).finally(() => {
      session.activeRenders = Math.max(0, session.activeRenders - 1);
      if (isActive(session)) drainRenderQueue(session);
    });
  }
}

async function renderPage(
  session: PdfViewerSession,
  pageState: PdfPageState
) {
  if (!isActive(session)) return;
  pageState.rendering = true;
  try {
    const pdfPage = await session.document.getPage(pageState.pageNumber);
    if (!isActive(session)) {
      pdfPage.cleanup();
      return;
    }

    pageState.page = pdfPage;
    const body = viewerBody.value;
    if (!body) return;
    const baseViewport = pdfPage.getViewport({ scale: 1 });
    const availableWidth = Math.max(220, body.clientWidth - 32);
    const cssWidth = Math.min(availableWidth, 1100);
    const viewport = pdfPage.getViewport({
      scale: cssWidth / baseViewport.width,
    });
    const maxPixelRatio = Math.sqrt(
      4000000 / Math.max(viewport.width * viewport.height, 1)
    );
    const pixelRatio = Math.max(
      1,
      Math.min(window.devicePixelRatio || 1, 2, maxPixelRatio)
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width * pixelRatio);
    canvas.height = Math.floor(viewport.height * pixelRatio);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;
    canvas.setAttribute(
      "aria-label",
      `${t("pdfViewer.page")} ${pageState.pageNumber}`
    );

    pageState.node.style.width = `${Math.floor(viewport.width)}px`;
    pageState.node.style.minHeight = `${Math.floor(viewport.height)}px`;
    const label = pageState.node.querySelector(".pdf-viewer__page-number");
    pageState.node.innerHTML = "";
    pageState.node.appendChild(canvas);
    if (label) pageState.node.appendChild(label);

    const canvasContext = canvas.getContext("2d", { alpha: false });
    if (!canvasContext) throw new Error("CANVAS_CONTEXT_UNAVAILABLE");
    const renderContext: Record<string, any> = { canvasContext, viewport };
    if (pixelRatio !== 1) {
      renderContext.transform = [pixelRatio, 0, 0, pixelRatio, 0, 0];
    }
    pageState.renderTask = pdfPage.render(renderContext);
    await pageState.renderTask.promise;
    if (isActive(session)) pageState.rendered = true;
  } catch (error: any) {
    if (
      error?.name !== "RenderingCancelledException" &&
      isActive(session)
    ) {
      pageState.node.classList.add("pdf-viewer__page--error");
      pageState.node.textContent = t("pdfViewer.pageError");
    }
  } finally {
    pageState.renderTask = null;
    pageState.rendering = false;
  }
}

function resetPage(pageState: PdfPageState, preserveLayout = true) {
  try {
    pageState.renderTask?.cancel();
  } catch (_) {
    // Rendering may already be cancelled during cleanup.
  }
  const canvas = pageState.node.querySelector("canvas");
  if (canvas) {
    canvas.width = 0;
    canvas.height = 0;
  }
  try {
    pageState.page?.cleanup();
  } catch (_) {
    // The page may already have been released by PDF.js.
  }

  pageState.page = null;
  pageState.renderTask = null;
  pageState.rendering = false;
  pageState.rendered = false;
  pageState.queued = false;
  pageState.node.classList.remove("pdf-viewer__page--error");
  if (!preserveLayout) {
    pageState.node.style.width = "";
    pageState.node.style.minHeight = "";
  }
  pageState.node.innerHTML = "";
  const label = document.createElement("span");
  label.className = "pdf-viewer__page-number";
  label.textContent = String(pageState.pageNumber);
  pageState.node.appendChild(label);
}

function releaseDistantPages(session: PdfViewerSession) {
  if (!isActive(session)) return;
  session.renderQueue = session.renderQueue.filter((pageNumber) => {
    const keep =
      Math.abs(pageNumber - session.currentPage) <= 2 ||
      session.nearbyPages.has(pageNumber);
    if (!keep) {
      const pageState = session.pageStates.get(pageNumber);
      if (pageState) pageState.queued = false;
    }
    return keep;
  });

  session.pageStates.forEach((pageState, pageNumber) => {
    if (
      pageState.rendered &&
      Math.abs(pageNumber - session.currentPage) > 2 &&
      !session.nearbyPages.has(pageNumber)
    ) {
      resetPage(pageState);
    }
  });
}

function setCurrentPage(session: PdfViewerSession, pageNumber: number) {
  if (!isActive(session) || !session.totalPages) return;
  syncPageControls(session, pageNumber);
  queuePage(session, session.currentPage, true);
  queuePage(session, session.currentPage - 1);
  queuePage(session, session.currentPage + 1);
  releaseDistantPages(session);
}

function observePages(session: PdfViewerSession) {
  const body = viewerBody.value;
  if (!body) return;
  session.observer = new IntersectionObserver(
    (entries) => {
      if (!isActive(session)) return;
      entries.forEach((entry) => {
        const pageNumber = Number(
          (entry.target as HTMLElement).dataset.page
        );
        if (entry.isIntersecting) session.nearbyPages.add(pageNumber);
        else session.nearbyPages.delete(pageNumber);
      });

      const bodyRect = body.getBoundingClientRect();
      if (session.navigationTarget != null) {
        const targetPage = session.navigationTarget;
        const targetNode = session.pageStates.get(targetPage)?.node;
        if (targetNode) {
          const targetScrollTop = Math.min(
            Math.max(targetNode.offsetTop - 12, 0),
            Math.max(body.scrollHeight - body.clientHeight, 0)
          );
          if (Math.abs(body.scrollTop - targetScrollTop) <= 2) {
            session.navigationTarget = null;
            syncPageControls(session, targetPage);
            queuePage(session, targetPage, true);
            return;
          }
        }
        return;
      }
      let closestPage = session.currentPage;
      let closestDistance = Number.POSITIVE_INFINITY;
      session.nearbyPages.forEach((pageNumber) => {
        const pageState = session.pageStates.get(pageNumber);
        if (!pageState) return;
        const rect = pageState.node.getBoundingClientRect();
        if (rect.bottom <= bodyRect.top || rect.top >= bodyRect.bottom) return;
        const distance = Math.abs(
          (rect.top + rect.bottom) / 2 -
            (bodyRect.top + bodyRect.bottom) / 2
        );
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = pageNumber;
        }
      });

      syncPageControls(session, closestPage);
      queuePage(session, closestPage, true);
      queuePage(session, closestPage - 1);
      queuePage(session, closestPage + 1);
      releaseDistantPages(session);
      [...session.nearbyPages]
        .sort(
          (left, right) =>
            Math.abs(left - closestPage) - Math.abs(right - closestPage)
        )
        .slice(0, 4)
        .forEach((pageNumber) => queuePage(session, pageNumber));
    },
    {
      root: body,
      rootMargin: "75% 0px",
      threshold: 0.01,
    }
  );
  session.pageStates.forEach((pageState) =>
    session.observer?.observe(pageState.node)
  );
}

function rerenderVisiblePages(session: PdfViewerSession) {
  if (!isActive(session) || !viewerBody.value) return;
  const nextWidth = viewerBody.value.clientWidth;
  if (Math.abs(nextWidth - session.viewerWidth) < 8) return;
  session.viewerWidth = nextWidth;
  session.renderQueue.length = 0;
  session.pageStates.forEach((pageState) => resetPage(pageState, false));
  setCurrentPage(session, session.currentPage);
  [...session.nearbyPages].forEach((pageNumber) =>
    queuePage(session, pageNumber)
  );
}

function scheduleResize(session: PdfViewerSession) {
  if (!isActive(session)) return;
  if (session.resizeTimer != null) window.clearTimeout(session.resizeTimer);
  session.resizeTimer = window.setTimeout(() => {
    session.resizeTimer = null;
    rerenderVisiblePages(session);
  }, 150);
}

function observeViewerResize(session: PdfViewerSession) {
  const body = viewerBody.value;
  if (!body) return;
  session.viewerWidth = body.clientWidth;
  if (typeof ResizeObserver !== "undefined") {
    session.resizeObserver = new ResizeObserver(() => scheduleResize(session));
    session.resizeObserver.observe(body);
  } else {
    session.resizeHandler = () => scheduleResize(session);
    window.addEventListener("resize", session.resizeHandler);
  }
}

function goToPage(pageNumber: number, smooth = false) {
  const session = activeSession;
  if (!session || !session.totalPages || !viewerBody.value) return;
  const targetPage = Math.min(
    Math.max(Number(pageNumber) || 1, 1),
    session.totalPages
  );
  session.navigationTarget = targetPage;
  setCurrentPage(session, targetPage);
  const target = session.pageStates.get(targetPage)?.node;
  if (target) {
    viewerBody.value.scrollTo({
      top: Math.max(target.offsetTop - 12, 0),
      behavior: smooth ? "smooth" : "auto",
    });
  }
}

function applyPageInput() {
  goToPage(Number(pageInput.value));
}

function createSession(): PdfViewerSession {
  return {
    disposed: false,
    loadingTask: null,
    document: null,
    observer: null,
    resizeObserver: null,
    resizeTimer: null,
    resizeHandler: null,
    pageStates: new Map(),
    nearbyPages: new Set(),
    renderQueue: [],
    activeRenders: 0,
    currentPage: 1,
    navigationTarget: null,
    totalPages: 0,
    viewerWidth: 0,
  };
}

async function openViewer() {
  if (!props.source || !isDisplayed.value) return;
  disposeActiveSession();
  const session = createSession();
  activeSession = session;
  previousFocus =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  document.body.classList.add("pdf-viewer-open");
  status.value = "loading";
  errorKey.value = "pdfViewer.loadError";
  currentPage.value = 1;
  totalPages.value = 0;
  pageInput.value = "1";

  await nextTick();
  if (!isActive(session)) return;
  pagesContainer.value?.replaceChildren();
  closeButton.value?.focus();

  try {
    const [pdfJs, data] = await Promise.all([
      loadPdfJs(),
      props.source.arrayBuffer(),
    ]);
    if (!isActive(session)) return;
    const bytes = new Uint8Array(data);
    const signature = new TextDecoder("ascii").decode(
      bytes.subarray(0, Math.min(bytes.byteLength, 5))
    );
    if (signature !== "%PDF-") {
      errorKey.value = "pdfViewer.unsupported";
      throw new Error("PDF_UNSUPPORTED");
    }

    session.loadingTask = pdfJs.getDocument({
      data: bytes,
      enableXfa: false,
      isEvalSupported: false,
    });
    session.document = await session.loadingTask.promise;
    if (!isActive(session)) return;

    session.totalPages = session.document.numPages;
    totalPages.value = session.totalPages;
    const fragment = document.createDocumentFragment();
    for (let pageNumber = 1; pageNumber <= session.totalPages; pageNumber += 1) {
      const pageNode = createPagePlaceholder(pageNumber);
      session.pageStates.set(pageNumber, {
        pageNumber,
        node: pageNode,
        page: null,
        renderTask: null,
        queued: false,
        rendering: false,
        rendered: false,
      });
      fragment.appendChild(pageNode);
    }
    pagesContainer.value?.appendChild(fragment);
    status.value = "ready";
    observePages(session);
    observeViewerResize(session);
    goToPage(1, false);
  } catch (error: any) {
    if (!isActive(session)) return;
    if (error?.message !== "PDF_UNSUPPORTED") {
      errorKey.value = "pdfViewer.loadError";
    }
    status.value = "error";
  }
}

function disposeSession(session: PdfViewerSession) {
  if (session.disposed) return;
  session.disposed = true;
  try {
    session.observer?.disconnect();
  } catch (_) {
    // Observer cleanup is best effort.
  }
  try {
    session.resizeObserver?.disconnect();
  } catch (_) {
    // Observer cleanup is best effort.
  }
  if (session.resizeHandler) {
    window.removeEventListener("resize", session.resizeHandler);
  }
  if (session.resizeTimer != null) window.clearTimeout(session.resizeTimer);
  session.renderQueue.length = 0;
  session.pageStates.forEach((pageState) => {
    try {
      pageState.renderTask?.cancel();
      const canvas = pageState.node.querySelector("canvas");
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
      pageState.page?.cleanup();
    } catch (_) {
      // Individual pages may already be disposed.
    }
  });
  session.pageStates.clear();
  try {
    const destroyPromise =
      session.document?.destroy() || session.loadingTask?.destroy();
    if (destroyPromise?.catch) void destroyPromise.catch(() => undefined);
  } catch (_) {
    // The PDF loading task may already be destroyed.
  }
}

function disposeActiveSession() {
  const session = activeSession;
  activeSession = null;
  if (session) disposeSession(session);
}

function restorePreviousFocus() {
  const focusTarget = previousFocus;
  previousFocus = null;
  if (focusTarget?.isConnected) {
    try {
      focusTarget.focus();
    } catch (_) {
      // Focus restoration must not prevent the viewer from closing.
    }
  }
}

function closeViewer() {
  if (!isDisplayed.value) return;
  viewerRoot.value?.classList.add("pdf-viewer--closing");
  locallyClosed.value = true;
  document.body.classList.remove("pdf-viewer-open");
  emit("close");
  disposeActiveSession();
  restorePreviousFocus();
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && isDisplayed.value) closeViewer();
}

watch(
  () => [props.show, props.source] as const,
  async ([show, source], previousValue) => {
    const previousShow = previousValue?.[0] ?? false;
    if (!show) {
      locallyClosed.value = false;
      document.body.classList.remove("pdf-viewer-open");
      disposeActiveSession();
      return;
    }
    if (!source || (locallyClosed.value && previousShow)) return;
    locallyClosed.value = false;
    await openViewer();
  },
  { immediate: true, flush: "post" }
);

window.addEventListener("keydown", handleKeydown);

onBeforeUnmount(() => {
  locallyClosed.value = true;
  document.body.classList.remove("pdf-viewer-open");
  window.removeEventListener("keydown", handleKeydown);
  disposeActiveSession();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (_unref(isDisplayed))
      ? (_openBlock(), _createElementBlock("section", {
          key: 0,
          ref: (_value, _refs) => {
            _refs['viewerRoot'] = _value
            viewerRoot.value = _value
          },
          class: "pdf-viewer",
          role: "dialog",
          "aria-modal": "true",
          "aria-labelledby": "pdf-viewer-title"
        }, [
          _createElementVNode("header", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, _toDisplayString(__props.title), 1),
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("button", {
                type: "button",
                class: "pdf-viewer__control",
                disabled: !_unref(canGoBack),
                "aria-label": _unref(t)('pdfViewer.firstPage'),
                title: _unref(t)('pdfViewer.firstPage'),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (goToPage(1)))
              }, " « ", 8, _hoisted_4),
              _createElementVNode("button", {
                type: "button",
                class: "pdf-viewer__control",
                disabled: !_unref(canGoBack),
                "aria-label": _unref(t)('pdfViewer.previousPage'),
                title: _unref(t)('pdfViewer.previousPage'),
                onClick: _cache[1] || (_cache[1] = ($event: any) => (goToPage(currentPage.value - 1)))
              }, " ‹ ", 8, _hoisted_5),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((pageInput).value = $event)),
                class: "pdf-viewer__page-input",
                type: "number",
                min: "1",
                max: Math.max(totalPages.value, 1),
                disabled: totalPages.value === 0,
                "aria-label": _unref(t)('pdfViewer.page'),
                onChange: applyPageInput,
                onKeydown: _withKeys(_withModifiers(applyPageInput, ["prevent"]), ["enter"])
              }, null, 40, _hoisted_6), [
                [_vModelText, pageInput.value]
              ]),
              _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)("pdfViewer.of")) + " " + _toDisplayString(totalPages.value || "–"), 1),
              _createElementVNode("button", {
                type: "button",
                class: "pdf-viewer__control",
                disabled: !_unref(canGoForward),
                "aria-label": _unref(t)('pdfViewer.nextPage'),
                title: _unref(t)('pdfViewer.nextPage'),
                onClick: _cache[3] || (_cache[3] = ($event: any) => (goToPage(currentPage.value + 1)))
              }, " › ", 8, _hoisted_8),
              _createElementVNode("button", {
                type: "button",
                class: "pdf-viewer__control",
                disabled: !_unref(canGoForward),
                "aria-label": _unref(t)('pdfViewer.lastPage'),
                title: _unref(t)('pdfViewer.lastPage'),
                onClick: _cache[4] || (_cache[4] = ($event: any) => (goToPage(totalPages.value)))
              }, " » ", 8, _hoisted_9)
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("button", {
                ref: (_value, _refs) => {
                  _refs['closeButton'] = _value
                  closeButton.value = _value
                },
                type: "button",
                class: "pdf-viewer__close",
                "aria-label": _unref(t)('common.close'),
                title: _unref(t)('common.close'),
                onClick: _withModifiers(closeViewer, ["stop","prevent"])
              }, " × ", 8, _hoisted_11)
            ])
          ]),
          _createElementVNode("div", {
            ref: (_value, _refs) => {
              _refs['viewerBody'] = _value
              viewerBody.value = _value
            },
            class: "pdf-viewer__body"
          }, [
            (status.value !== 'ready')
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["pdf-viewer__state", { 'pdf-viewer__state--error': status.value === 'error' }]),
                  role: "status"
                }, _toDisplayString(_unref(statusMessage)), 3))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              ref: (_value, _refs) => {
                _refs['pagesContainer'] = _value
                pagesContainer.value = _value
              },
              class: "pdf-viewer__pages"
            }, null, 512)
          ], 512)
        ], 512))
      : _createCommentVNode("", true)
  ]))
}
}

})